import constants from "imports/desktopConstants"

// node fill and stroke colors
const DEFAULT_STROKE_COLOR = "white"
const DEFAULT_STROKE_WIDTH = "2px"

// force simulation
const GRAVITY = 0.15
const FRICTION = 0.7
const CHARGE = -700

// max number of times we'll let this.force call the 'tick' function
const MAX_REPS = 400000

// resize params
const MIN_ZOOM = 0.1
const MAX_ZOOM = 10
const ZOOM_INCRMENT = 0.25
const GRAPH_PERCENTAGE_OF_BOUNDING_BOX = 0.8

/**
 *  Network Graph - handles all funky d3 mutations of this.nodes, this.links
 *      to assure that a node/link stays the same node/link through transitions
 *      and data updates.
 *
 *  NOTE: while this looks & feels like a React Component, it's not: it's
 *  a regular JS class that gets instantiated with props
 *
 *  Dynamic Functionality:
 *      Pan around by clicking white-space and dragging mouse around
 *      Move node by clicking and dragging a node
 *      Zoom with pinch/pull/scrolll if using trackpad
 *          and zoom with scroll is using a mouse
 */
export default class Dynamo {
    constructor(props) {
        this.props = props
        this.setup()

        return {
            updateData: this.updateData.bind(this),
            updateProps: this.updateProps.bind(this),
            zoomReset: this.zoomReset.bind(this),
            zoomIn: this.zoomIn.bind(this),
            zoomOut: this.zoomOut.bind(this),
        }
    }

    updateProps(props) {
        this.props = props
        this.update()
    }

    updateData(data) {
        // setup new maps/lists to save time
        // mak node.id to node
        this.mapIdToNode = this.nodes.reduce((current, node) =>
            current[node.id] = node, {}
        )
        // map `${link.source.id}-${link.target.id}` to link
        this.mapIdToLink = this.links.reduce((current, link) =>
            current[`${link.source}-${link.target}`] = link, {}
        )

        // remove obsolete nodes and all the links they are attached to
        this.removeObsoleteNodesAndCorrespondingLinks(data.nodes)
        // add all the new nodes
        this.addNodesIfNew(data.nodes.map(({ id }) => id))

        /*
            TODO: optimize link update/add/remove
         */
        this.removeAllLinks()
        data.links.forEach(({ source, target, frequency, label, frequencyField }) =>
            this.addLink(source, target, frequency, label, frequencyField)
        )

        // If we are updating the data, we want to update the nodes and reset
        // the zoom.
        this.update().then(() => {
            this.zoomReset()
        })
    }

    // find nodes and links that are obsolete and remove them
    removeObsoleteNodesAndCorrespondingLinks(newNodes) {
        const currentNodeIds = this.nodes.map(({id}) => id)
        const newNodeIds = newNodes.map(({id}) => id)
        const nodesToRemove = currentNodeIds.filter(node => !newNodeIds.includes(node))

        const linksToRemove = Object.assign([], nodesToRemove)
            .reduce((currentList, nodeId) =>
                currentList.concat(
                    Object.assign([], this.links).filter((source, target) => (source === nodeId || target === nodeId))
                ), []
            )

        nodesToRemove.forEach(nodeId => this.removeNode(nodeId))
        linksToRemove.forEach((linkId) => {
            const [sourceId, targetId] = linkId.split('-to-')
            this.removeLink(sourceId, targetId)
        })
    }

    setup() {
        this.resetGraph()

        const svg = d3.select(`#${this.props.graphId}`)
                .append("svg:svg")
                .attr("width", this.props.width)
                .attr("height", this.props.height)
                .attr("id", "svg")
                .attr("pointer-events", "all")
                .attr("viewBox", "0 0 " + this.props.width + " " + this.props.height)
                .attr("perserveAspectRatio", "xMinYMid")
                // is user clicks whitespace, then stop the animation - makes it harder
                // to selected a link
                .on("click", () => this.force.stop())

        this.vis = svg.append('svg:g')

        // setup a group for links and nodes, to make sure nodes are always above links
        this.vis.append("g").attr("class", "all-nodes-and-links")

        if (this.props.allowZoom) {
            this.zoom = d3.behavior.zoom().scaleExtent([MIN_ZOOM, MAX_ZOOM])
            // zoom translates and scales the container inside the svg
            this.zoom.on('zoom', () => {
                this.vis.attr(
                    'transform',
                    `translate(${d3.event.translate}) scale(${d3.event.scale})`
                )
                // inversely scale the font so that it is always the same size no matter the zoom
                const node = this.vis
                    .select("g.all-nodes-and-links")
                    .selectAll("g.network-graph-node text")
                    .attr("font-size", (15 / d3.event.scale) + "px")

            })
            // attach the zoom handler to the svg
            svg.call(this.zoom)
        }

        this.force = d3.layout.force()

        this.nodes = this.force.nodes()
        this.links = this.force.links()

        this.update()
    }

    zoomTransition(scale, translate = undefined, transitionDuration = 1000) {
        if (!this.zoom) {
            // viz is not ready yet - can't zoom during initial bouncing of nodes into initial location
            return
        }
        // Smooth transition scale and coordinates
        this.vis
            .transition()
            .duration(transitionDuration) // milliseconds
            .call(
                translate
                    ? this.zoom.translate(translate).scale(scale).event
                    : this.zoom.scale(scale).event
            )
    }

    zoomIn() {
        this.zoomTransition(this.zoom.scale() + ZOOM_INCRMENT)
    }

    zoomOut() {
        this.zoomTransition(this.zoom.scale() - ZOOM_INCRMENT)
    }

    zoomReset() {
        if (!this.props.allowZoom) {
            return
        }

        // Set the scale and coordinates of the visualization to fit in the center
        // of the bounding box.

        const bounds = this.vis.node().getBBox()
        const parent = this.vis.node().parentElement

        const availableWidth = parent.clientWidth || parent.parentNode.clientWidth
        const availableHeight = parent.clientHeight || parent.parentNode.clientHeight

        const graphWidth = bounds.width
        const graphHeight = bounds.height

        const midX = bounds.x + graphWidth / 2
        const midY = bounds.y + graphHeight / 2

        if (graphWidth === 0 || graphHeight === 0) {
            console.log("nothing to fit")
            return
        }

        // Scale the graph to be some percent of the parent element
        const scale = GRAPH_PERCENTAGE_OF_BOUNDING_BOX / Math.max(graphWidth / availableWidth, graphHeight / availableHeight)

        // Translate the graph to the midpoint of the bounding box
        const translate = [availableWidth / 2 - scale * midX, availableHeight / 2 - scale * midY]

        this.zoomTransition(scale, translate)
    }

    update() {
        // Often we may want to have some functionality that executes after the
        // graph has successfully updated and the nodes are all appended. At the
        // end of this function, this promise resolves after exactly 1 second to
        // give some time for d3 to go through the work of appending the nodes
        // and edges.
        return new Promise((resolve, reject) => {
            // get d3 selections of elements to pass in mouseevents
            const getSvgNode = (node) => this.vis.select(`#Node-${node.id}`)
            const getSvgNodeWithId = (nodeId) => this.vis.select(`#Node-${nodeId}`)
            const getSvgLink = (link) => this.vis.select(`#${link.source.id}-${link.target.id}`)
            // get d3 selcections array of all of this nodes neighboring nodes
            // (e.g. can highlight these neighbors when mousing over a node with a darkened stroke)
            const getSvgNodesNeighbors = (node) => {
                // iterate through all the links to find links connected to this node
                const neighborIds = []
                this.links.forEach((link) => {
                    // if this link if connected to our node, then neighbor is on the other end of the link
                    if (link.source.id === node.id) {
                        neighborIds.push(link.target.id)
                    }
                    else if (link.target.id === node.id) {
                        neighborIds.push(link.source.id)
                    }
                })
                return neighborIds.map(neighborId => getSvgNodeWithId(neighborId))
            }
            // get d3 selections array of all the links connected to this node
            const getSvgNodeLinks = node =>
                // iterate through all the links to find links connected to this node
                Object.assign([], this.links).reduce((current, link) => (
                    (link.source.id === node.id || link.target.id === node.id)
                        ? current.concat([getSvgLink(link)])
                        : current
                ), [])

            // get d3 selections array for the two nodes that a link connects
            const getSvgLinkNeighbors = link =>
                [
                    getSvgNodeWithId(link.source.id),
                    getSvgNodeWithId(link.target.id)
                ]

            /*
                link -- enter, update, exit
             */
            const getAllLinkMouseEventArguments = link => [
                link,
                d3.event,
                getSvgLink(link),
                getSvgLinkNeighbors(link)
            ]

            // if we're not allowing zoom, then we should scale the graph based on
            // the number of nodes
            if (!this.props.allowZoom) {

                // when you scale, you also "translate" -- i.e. scaling something
                // smaller "moves" it up and to the left
                // to counteract this, we need to turn a scale into a set of translate
                // coordinates
                const getTranslatefromScale = (scale, width, height) => {
                    // if the scale is .5, we need to shift over .25
                    // if the scale is .1, we need to shift over .45
                    // this means that we take 1 - scale and divide by 2
                    const scaleFactor = (1 - scale) / 2
                    return [scaleFactor * width, scaleFactor * height]
                }

                // to determine this function we used 
                // https://www.wolframalpha.com/input/?i=best+fit+of+%5B(368,+140099.07,+0.615),(522,+137812.01,+0.85),(522,+618655.066,+0.44),(739,+140099.07,+1.2),(884,+139359.668,+1.4),(884,+91676.17,+1.53),(281,+91676.178,+0.5)%5D
                // attempt 2: https://www.wolframalpha.com/input/?i=best+fit+%5B(522,+100,+0.85),+(742,+100,+0.9),+(884,+100,+1.45),+(345,+435,+0.305),+(782,+435,+0.68),+(880,+435,+0.75),+(656,+66,+1.183),+(884,+66,+1.53),+(345,+66,+0.679)%5D
                // These are 3 good cases to check scaling with:
                //      http://localhost:8000/official/412251/relationships/  senator (Sen. John Barrasso (R-WY))
                //      http://localhost:8000/official/984432/relationships/  state senator (MN Sen. Jim Abeler (R-MN-035))
                //      http://localhost:8000/official/412531/relationships/  rep (Rep. Doug Collins (R-GA-9)) <-- this dude is ridiculous to scale
                const widthCoeff = 0.00121034 // scale increases with increasing width
                const nodeCoeff = -0.0016259 // scale decreases with increasing num nodes
                const intercept = 0.411826

                // numNodes needs to matter less when the width is sufficiently smaller than the height
                const numNodes = (this.props.width < this.props.height - 100 && this.nodes.length > 400)
                    ? this.nodes.length - 100
                    : this.nodes.length
                const scale = (
                    widthCoeff * this.props.width +
                    nodeCoeff * numNodes +
                    intercept
                )

                const [ xTranslate, yTranslate ] = getTranslatefromScale(scale, this.props.width, this.props.height)

                this.vis
                    .select("g.all-nodes-and-links")
                    .attr(
                        'transform',
                        `translate(${xTranslate},${yTranslate}) scale(${scale})`
                    )
            }

            const link = this.vis
                .select("g.all-nodes-and-links")
                .selectAll("line")
                .data(this.links, link => link.source.id + "-" + link.target.id)

            link.enter()
                .append("line")
                .attr("id", d => d.source.id + "-" + d.target.id)
                .attr("class", "network-graph-link")
                .attr(this.props.getLinkEnterAttributes())

            link.attr(this.props.getLinkTransitionAttributes())

            link.exit().remove()

            /*
                node -- enter, update, exit
             */
            // we pass the same argument to every mouseevent handler, so gathering here
            const getAllNodeMouseEventArguments = node => [
                node,
                d3.event,
                getSvgNode(node),
                getSvgNodesNeighbors(node),
                getSvgNodeLinks(node)
            ]

            const node = this.vis
                .select("g.all-nodes-and-links")
                .selectAll("g.network-graph-node")
                .data(this.nodes, d => d.id)

            const nodeEnter = node.enter()
                .append("g")
                .attr("class", "network-graph-node")

            // add circle
            nodeEnter
                .append("svg:circle")
                .attr("id", d => `Node-${d.id}`)
                .attr("class", "nodeStrokeClass")
                .attr(this.props.getNodeEnterAttributes())
                /* NOTE!: add the mouse event handlers to the node, not the group since the text
                    label can make it confusing what is being selected */
                .on("mouseover", node =>
                    this.props.handleNodeOnMouseover(
                        ...getAllNodeMouseEventArguments(node)
                    )
                )
                .on("mouseout", node =>
                    this.props.handleNodeOnMouseout(
                        ...getAllNodeMouseEventArguments(node)
                    )
                )
                .on("click", (node) => {
                    return this.props.handleNodeOnClick(node, d3.event, getSvgNode(node))
                })

            // add text on enter
            nodeEnter
                .insert("svg:text", ".network-graph-link", ".network-graph-node circle")
                .attr("class", "textClass")
                // set pointer-events to none, so the mouse events will pass to
                // svg elements underneath the text (to a node or link below)
                .attr("pointer-events", "none")
                .text(node => this.props.getNodeLabel(node.id))
                .attr("x", 14)
                .attr("y", ".31em")
                // set paint-order so we can get a white stroke around text
                // to make it easier to read
                .attr("paint-order", "stroke")
                .attr("stroke", DEFAULT_STROKE_COLOR)
                .attr("stroke-width", DEFAULT_STROKE_WIDTH)

            node.selectAll("text")
                .attr("text-anchor", node => "start")
                .attr("visibility", node => (
                    this.props.isLabelVisible(node) ? "visible" : "hidden"
                ))

            node.selectAll("circle")
                .attr(this.props.getNodeEnterAttributes())

            node.exit().remove()

            this.keepNodesOnTop()

            // force layout update logic
            this.force.on("tick", (e) => {

                // if we don't allow zooming, we want the whole graph to fit
                // into the space allocated for it
                // as a result, we want to transform the whole graph to fit appropriately
                const stretch = this.props.allowZoom ? 1 : (this.props.height / this.props.width)

                node.attr("transform", node => {

                    // if there is a central node, center it in the viz
                    if (node.id === this.props.centerNodeId) {
                        node.x = this.props.width / 2
                        node.y = this.props.height / 2 / stretch
                    }

                    return `translate(${node.x},${node.y * stretch})`
                })

                link.attr("x1", link => link.source.x)
                    .attr("y1", link => link.source.y * stretch)
                    .attr("x2", link => link.target.x)
                    .attr("y2", link => link.target.y * stretch)

                // cut animation short if its running too long
                this.currentTickRep += 1
                if (this.currentTickRep >= MAX_REPS) {
                    this.force.stop()
                }
            })

            this.currentTickRep = 0

            // Restart the force layout, resolve the promise
            this.force
                .gravity(this.props.gravity || GRAVITY)
                .charge(this.props.charge || CHARGE)
                // .chargeDistance(CHARGE_DISTANCE)
                .friction(this.props.friction || FRICTION)
                .linkDistance(d => {return this.props.scales.linkDistance(d.value)})
                .linkStrength(d => {return this.props.scales.linkStrength(d.value)})
                .size([this.props.width, this.props.height])
                .start()

            // Resolve the promise after some time for some ersatz asynchronicity.
            setTimeout(() => { resolve() }, 250)
        })
    }

    // Add and remove elements on the graph object
    addNode(id) {
        this.nodes.push({ id })
    }

    addNodesIfNew(nodeIdList) {
        const nodesToAdd = []
        nodeIdList.forEach((nodeId) => {
            const isNew = Boolean(!this.mapIdToNode[nodeId])
            if (isNew) {
                nodesToAdd.push(nodeId)
            }
        })
        nodesToAdd.forEach(nodeId => this.addNode(nodeId))
    }

    removeNode(id) {
        let i = 0
        let n = this.findNode(id)
        while (i < this.links.length) {
            if ((this.links[i]['source'] == n) || (this.links[i]['target'] == n)) {
                this.links.splice(i, 1)
            }
            else i++
        }
        this.nodes.splice(this.findNodeIndex(id), 1)
    }

    removeLink(source, target) {
        this.links.forEach((link, i) => {
            if (link.source.id == source && link.target.id == target) {
                this.links.splice(i, 1)
            }
        })
    }

    removeAllLinks() {
        this.links.splice(0, this.links.length)
        this.update()
    }

    removeAllNodes() {
        this.nodes.splice(0, this.links.length)
        this.update()
    }

    addLink(source, target, value, label, frequencyField) {
        this.links.push({
            "source": this.findNode(source),
            "target": this.findNode(target),
            "value": value,
            label,
            frequencyField,
        })
    }

    findNode(id) {
        const index = this.findNodeIndex(id)
        return this.nodes[index]
    }

    findNodeIndex(id) {
        return this.nodes.map(({ id }) => id).indexOf(id)
    }

    resetGraph() {
        d3.select(`#${this.props.graphId} svg`).remove()
    }

    keepNodesOnTop() {
        // using function() notation to preserve 'this' as a reference to the DOM node
        $(".nodeStrokeClass").each(function(index) {
            let gnode = this.parentNode
            gnode.parentNode.appendChild(gnode)
        })
    }
}
